import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { Label, Pie, PieChart } from 'recharts';
import Cookies from 'js-cookie';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../../components/ui/card';
import { ChartConfig, ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../components/ui/chart';
import { TOKEN_NAME } from '../../../constants';

const chartConfig = {
  //   inactiveIssues: {
  //     label: 'Inactive Orders',
  //     color: 'hsl(var(--chart-1))',
  //   },
  noAction: {
    label: 'No Action',
    color: 'hsl(var(--chart-2))',
  },
  merchantAction: {
    label: 'Merchant',
    color: 'hsl(var(--chart-3))',
  },
  carrierAction: {
    label: 'Carrier',
    color: 'hsl(var(--chart-4))',
  },
  both: {
    label: 'Both',
    color: 'hsl(var(--chart-5))',
  },
} satisfies ChartConfig;

const InActiveStats = ({ range }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, [range]);

  const fetchDashboardStats = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URI}/dashboard/inactive-order-stats?range=${range}`, {
        headers: {
          Authorization: `Bearer ${Cookies.get(TOKEN_NAME)}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch dashboard data');
      }

      const fetchedData = await response.json();
      setData(fetchedData);
    } catch (err) {
      console.error(err);
    }
  };

  const chartData = useMemo(() => {
    if (!data) return [];
    return [
      { category: 'No Action', count: data.noAction, fill: 'hsl(var(--chart-2))' },
      { category: 'Merchant', count: data.merchantAction, fill: 'hsl(var(--chart-3))' },
      { category: 'Carrier', count: data.carrierAction, fill: 'hsl(var(--chart-4))' },
      { category: 'Both', count: data.both, fill: 'hsl(var(--chart-5))' },
    ];
  }, [data]);

  const totalInactiveOrders = useMemo(() => {
    return data?.inactiveIssues || 0;
  }, [data]);

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle>Inactive orders</CardTitle>
        <CardDescription>Total inactive orders in this period</CardDescription>
      </CardHeader>
      <CardContent className="flex-1 pb-0">
        {totalInactiveOrders === 0 ? (
          <div className="flex flex-col items-center justify-center h-full">
            <h3 className="text-2xl font-bold">0</h3>
            <p className="text-sm text-muted-foreground">No inactive orders</p>
          </div>
        ) : (
          <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[250px]">
            <PieChart>
              <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} />
              <Pie data={chartData} dataKey="count" nameKey="category" innerRadius={60} strokeWidth={5}>
                <Label
                  content={({ viewBox }) => {
                    if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                      return (
                        <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle" dominantBaseline="middle">
                          <tspan x={viewBox.cx} y={viewBox.cy} className="fill-foreground text-3xl font-bold">
                            {totalInactiveOrders.toLocaleString()}
                          </tspan>
                          <tspan x={viewBox.cx} y={(viewBox.cy || 0) + 24} className="fill-muted-foreground">
                            Inactive orders
                          </tspan>
                        </text>
                      );
                    }
                  }}
                />
              </Pie>
            </PieChart>
          </ChartContainer>
        )}
      </CardContent>
      <CardFooter className="flex-col gap-2 text-sm">
        <div className="leading-none text-muted-foreground">Showing total inactive orders for this period</div>
      </CardFooter>
    </Card>
  );
};

export default InActiveStats;
